/**
 * ASOs emitidos — listagem com filtro por empresa/busca + abrir PDF.
 * O PDF sai do snapshot congelado na emissão (regeneração idêntica).
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { apiFetchBlob } from "@/auth/api";
import { useAsos, useEmpresas } from "@/hooks/useApi";
import {
  RESULTADO_PARECER_ROTULOS,
  STATUS_ASO_BADGE,
  TIPO_EXAME_ROTULOS,
} from "@/types/api";
import { fmtData } from "@/pages/Pendencias";

export function AsosPage() {
  const { data: empresas } = useEmpresas();
  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const { data: asos, isLoading, error } = useAsos({ empresa_id: empresaId, q: q || undefined });
  const [erroPdf, setErroPdf] = useState<string | null>(null);

  async function abrirPdf(asoId: number) {
    setErroPdf(null);
    try {
      const { blob } = await apiFetchBlob(`/asos/${asoId}/pdf`);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setErroPdf((e as Error).message);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-semibold">ASOs</h2>
        <p className="text-sm text-ink-muted">
          Atestados de Saúde Ocupacional emitidos — o PDF sai do snapshot congelado na emissão.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <label className="mb-1 block text-xs font-medium text-ink-muted">Empresa</label>
          <select className="input" value={empresaId ?? ""}
            onChange={(e) => setEmpresaId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">todas as empresas</option>
            {(empresas ?? []).map((e) => (
              <option key={e.id} value={e.id}>{e.razao_social}</option>
            ))}
          </select>
        </div>
        <div className="w-72">
          <label className="mb-1 block text-xs font-medium text-ink-muted">Busca</label>
          <input className="input" placeholder="número ou colaborador…" value={q}
            onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {error ? <p className="text-sm text-anarosa">{(error as Error).message}</p> : null}
      {erroPdf ? <p className="text-sm text-anarosa">{erroPdf}</p> : null}
      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}

      {asos && asos.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3">Número</th>
                <th className="px-4 py-3">Colaborador</th>
                <th className="px-4 py-3">Empresa</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Parecer</th>
                <th className="px-4 py-3">Gerado em</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {asos.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs font-semibold">{a.numero}</td>
                  <td className="px-4 py-2 font-medium">{a.colaborador}</td>
                  <td className="px-4 py-2 text-ink-muted">{a.empresa}</td>
                  <td className="px-4 py-2">{TIPO_EXAME_ROTULOS[a.tipo]}</td>
                  <td className="px-4 py-2 text-ink-muted">
                    {a.resultado ? RESULTADO_PARECER_ROTULOS[a.resultado] : "—"}
                  </td>
                  <td className="px-4 py-2 text-ink-muted">{fmtData(a.gerado_em)}</td>
                  <td className="px-4 py-2">
                    <span className={STATUS_ASO_BADGE[a.status].classe}>
                      {STATUS_ASO_BADGE[a.status].rotulo}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button className="btn-secondary px-2 py-1 text-xs" onClick={() => abrirPdf(a.id)}>
                        Abrir PDF
                      </button>
                      <Link className="btn-secondary px-2 py-1 text-xs" to={`/atendimentos/${a.atendimento_id}`}>
                        Atendimento
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !isLoading && !error ? (
        <p className="text-sm text-ink-muted">Nenhum ASO emitido ainda.</p>
      ) : null}
    </div>
  );
}
